import { useQuery } from '@tanstack/react-query';

import { useDebouncedValue } from '#src/app/shared/hooks/debounce';
import { useAutocompleteService, useSessionToken } from '#src/app/shared/hooks/googlePlaces';
import { useLocale } from '#src/app/shared/hooks/l10n';

import { getPlacePredictions } from '../api/getPlacePredictions';
import { parsePlaceDetails } from '../shared/placeDetailsParser';
import type { GeoScope, PlaceWithDetails, PlaceWithPrediction } from '../shared/types';

const DEFAULT_INPUT_LENGTH_API_CALL_THRESHOLD = 3;
const DEBOUNCE_DELAY_MS = 350;

type PlaceWithDetailsFieldHookProps = {
	geoScope?: GeoScope;
	placeInputValue: PlaceWithDetails | null;
	onChange: (place: PlaceWithDetails | null) => void;
	inputLengthApiCallThreshold?: number;
};

type UsePlaceWithDetailsFieldReturnProps = {
	places: PlaceWithPrediction[];
	isLoading: boolean;
	onSelectionChange: (selectedPlaceId: string) => Promise<void>;
	onInputChange: (input: string) => void;
};

export const usePlaceWithDetailsField = ({
	geoScope,
	placeInputValue,
	onChange,
	inputLengthApiCallThreshold = DEFAULT_INPUT_LENGTH_API_CALL_THRESHOLD,
}: PlaceWithDetailsFieldHookProps): UsePlaceWithDetailsFieldReturnProps => {
	const { sessionToken, resetSessionToken } = useSessionToken();
	const locale = useLocale();
	const autocompleteService = useAutocompleteService();

	const inputValue = placeInputValue?.displayName || '';
	const debouncedInputValue = useDebouncedValue(inputValue, DEBOUNCE_DELAY_MS);

	// eslint-disable-next-line @tanstack/query/exhaustive-deps -- sessionToken/autocompleteService are stable and intentionally omitted from the key
	const { isLoading, data, error } = useQuery({
		queryKey: ['google-places-v2', debouncedInputValue, locale, geoScope, inputLengthApiCallThreshold],
		queryFn: async () => {
			return getPlacePredictions(
				{ input: debouncedInputValue, locale, geoScope, sessionToken },
				autocompleteService,
				inputLengthApiCallThreshold,
			);
		},
	});

	return {
		places: error ? [] : data || [],
		onSelectionChange: async (selectedPlaceId) => {
			const selectedPlace = data?.find((place) => place.googlePlacesId === selectedPlaceId) || null;

			if (!selectedPlace?.placePrediction) {
				return;
			}
			const place = selectedPlace.placePrediction.toPlace();
			await place.fetchFields({ fields: ['addressComponents'] });
			if (!place.addressComponents) {
				return;
			}
			const { addressComponents } = place;
			const placeWithDetails = parsePlaceDetails({ addressComponents });
			onChange({
				...selectedPlace,
				...placeWithDetails,
			});
			if (resetSessionToken) {
				resetSessionToken();
			}
		},
		onInputChange: (newInputValue) => {
			onChange({ displayName: newInputValue } as PlaceWithDetails);
		},
		isLoading,
	};
};
